import type { CSSProperties } from "react";
import type { BoardLegendItem } from "./legendTypes";

/** Calendar aliases that reuse another legend row. */
const LINE_ALIAS: Record<string, string> = {
  talk: "you",
  ip: "gx",
};

export function legendLineKey(lineKey: string) {
  return LINE_ALIAS[lineKey] ?? lineKey;
}

export function findLegendItem(
  items: BoardLegendItem[],
  query: { lineKey?: string; token?: string },
) {
  if (query.token) {
    const byToken = items.find((item) => item.token === query.token);
    if (byToken) return byToken;
  }
  if (!query.lineKey) return undefined;
  const key = legendLineKey(query.lineKey);
  return items.find((item) => item.line_key === key);
}

function hex(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function solidPaintStyle(
  item: BoardLegendItem,
): CSSProperties | undefined {
  const style: CSSProperties = {};
  const bg = hex(item.bar_hex);
  const fg = hex(item.bar_text_hex);
  if (bg) style.backgroundColor = bg;
  if (fg) style.color = fg;
  return Object.keys(style).length ? style : undefined;
}

export function dashedPaintStyle(
  item: BoardLegendItem,
): CSSProperties | undefined {
  const style: CSSProperties = {};
  const border = hex(item.outline_hex);
  const fg = hex(item.outline_text_hex);
  if (border) style.borderColor = border;
  if (fg) style.color = fg;
  if (border) {
    style.backgroundColor = "transparent";
  }
  return Object.keys(style).length ? style : undefined;
}

export function barPaintStyle(
  item: BoardLegendItem | undefined,
): CSSProperties | undefined {
  if (!item) return undefined;
  return item.visual_style === "dashed"
    ? dashedPaintStyle(item)
    : solidPaintStyle(item);
}

export function faceTagStyle(item: BoardLegendItem | undefined) {
  return barPaintStyle(item);
}

export function softChipStyle(
  item: BoardLegendItem | undefined,
): CSSProperties | undefined {
  if (!item) return undefined;
  const style: CSSProperties = {};
  const bg = hex(item.soft_chip_bg);
  const fg = hex(item.soft_chip_text);
  const dot = hex(item.soft_dot_hex);
  if (bg) style.backgroundColor = bg;
  if (fg) style.color = fg;
  if (dot) style.borderColor = dot;
  return Object.keys(style).length ? style : undefined;
}

export function railColor(item: BoardLegendItem | undefined, fallback: string) {
  return hex(item?.bar_hex) ?? hex(item?.soft_dot_hex) ?? fallback;
}

function parseRgb(value: string) {
  const raw = value.startsWith("#") ? value.slice(1) : value;
  if (raw.length !== 6 || /[^0-9a-fA-F]/.test(raw)) return null;
  return {
    r: Number.parseInt(raw.slice(0, 2), 16),
    g: Number.parseInt(raw.slice(2, 4), 16),
    b: Number.parseInt(raw.slice(4, 6), 16),
  };
}

/** Mix `hexA` toward `hexB` by `amountB` (0–1). Used for ~10% expand washes. */
export function mixHex(hexA: string, hexB: string, amountB: number) {
  const a = parseRgb(hexA);
  const b = parseRgb(hexB);
  if (!a || !b) return hexA;
  const mix = (left: number, right: number) =>
    Math.round(left + (right - left) * amountB);
  return `#${[mix(a.r, b.r), mix(a.g, b.g), mix(a.b, b.b)]
    .map((n) => n.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function washStyle(
  item: BoardLegendItem | undefined,
): CSSProperties | undefined {
  if (!item) return undefined;
  const bar = hex(item.bar_hex);
  if (bar) return { backgroundColor: mixHex(bar, "#ffffff", 0.9) };
  const soft = hex(item.soft_chip_bg);
  return soft ? { backgroundColor: soft } : undefined;
}
